import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useEffect,
  useState,
} from "react";
import { AnimatePresence } from "framer-motion";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useWebSocket, WebSocketProvider, socketClient } from "@/services/websocket";
import {
  useCollaboration as useCollaborationHook,
  usePresence,
  useConflictResolution,
} from "@/hooks/collaboration";
import {
  PresenceOverlay,
  SimplePresenceOverlay,
} from "../collaboration/PresenceOverlay";
import { PresenceAdapter } from "../collaboration/PresenceAdapter";
import { RealtimeCursors } from "../collaboration/RealtimeCursors";
import { ConflictAlertsContainer } from "../collaboration/ConflictAlert";
import { MergeEditor, MergeEditorBackdrop } from "../collaboration/MergeEditor";
import {
  Component,
  CollaborationEvent,
  CollaborationEventType,
  Position,
  User,
} from "@/types";
import { DragConflict, UserSession } from "@/stores/collaboration";

interface CollaborationContextValue {
  // Connection state
  isActive: boolean;
  isConnected: boolean;

  // Users and presence
  activeUsers: UserSession[];
  currentUser: UserSession | null;

  // Actions
  startCollaboration: (projectId: string) => Promise<void>;
  stopCollaboration: () => Promise<void>;
  updateCursor: (position: Position) => void;
  updateSelection: (componentIds: string[]) => void;

  // Legacy compatibility methods
  isComponentLocked: (componentId: string) => boolean;
  getComponentLockOwner: (componentId: string) => UserSession | null;
  requestComponentLock: (componentId: string) => Promise<boolean>;
  releaseComponentLock: (componentId: string) => void;
  handleDragConflict: (
    componentId: string,
    action: "start" | "resolve",
  ) => void;
  getUserCursor: (userId: string) => Position | null;
  broadcastCursorPosition: (position: Position) => void;
  resolveDragConflict: (
    conflictId: string,
    resolution: "merge" | "reject" | "queue",
  ) => void;
}

const CollaborationContext = createContext<CollaborationContextValue | null>(
  null,
);

export const useCollaborationLegacy = () => {
  const context = useContext(CollaborationContext);
  if (!context) {
    throw new Error(
      "useCollaborationLegacy must be used within CollaborationProvider",
    );
  }
  return context;
};

interface CollaborationProviderProps {
  children: React.ReactNode;
  currentUser: User;
  projectId?: string;
  autoConnect?: boolean;
  showUI?: boolean;
  mobileOptimized?: boolean;
}

const CollaborationProviderInner: React.FC<CollaborationProviderProps> = ({
  children,
  currentUser,
  projectId,
  showUI = true,
  mobileOptimized = false,
}) => {
  const { components, selectedComponents } = useEditorStore();

  // Unified collaboration hook — single source of truth
  const collaboration = useCollaborationHook();
  const presence = usePresence();
  const conflictResolution = useConflictResolution();

  const canvasRef = useRef<HTMLDivElement>(null);

  // UI state
  const [showMergeEditor, setShowMergeEditor] = useState(false);
  const [activeMergeConflict, setActiveMergeConflict] =
    useState<DragConflict | null>(null);
  const [showDetailedInfo, setShowDetailedInfo] = useState(false);
  const [showCollaborationNotification, setShowCollaborationNotification] =
    useState(false);
  const [collaborationMessage, setCollaborationMessage] = useState("");
  const [collaborationAttempted, setCollaborationAttempted] = useState(false);
  const [lastFailedProjectId, setLastFailedProjectId] = useState<string | null>(null);
  const collaborationInitialized = useRef(false);
  const currentProjectIdRef = useRef<string | null>(null);
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // User interaction handlers
  const onUserClick = useCallback(
    (userId: string) => {
      setShowDetailedInfo(!showDetailedInfo);
    },
    [showDetailedInfo],
  );

  // Auto-start collaboration when projectId, user, and WebSocket are available
  useEffect(() => {
    if (collaborationInitialized.current && currentProjectIdRef.current === projectId) {
      return;
    }

    if (!projectId || !currentUser) {
      return;
    }

    if (lastFailedProjectId === projectId) {
      return;
    }

    if (collaborationAttempted && currentProjectIdRef.current === projectId) {
      return;
    }

    const now = Date.now();
    const lastAttemptTime = parseInt(sessionStorage.getItem(`collab_attempt_${projectId}`) || "0");
    const cooldownPeriod = 2000;

    if (now - lastAttemptTime < cooldownPeriod) {
      return;
    }

    collaborationInitialized.current = true;
    currentProjectIdRef.current = projectId;
    setCollaborationAttempted(true);
    sessionStorage.setItem(`collab_attempt_${projectId}`, now.toString());

    const checkAndStartCollaboration = async () => {
      let attempts = 0;
      const maxAttempts = 10;

      while (attempts < maxAttempts) {
        if (socketClient.isConnected()) {
          break;
        }

        attempts++;
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      try {
        await collaboration.startCollaboration(projectId);
        setCollaborationMessage("Collaboration enabled - real-time editing active");
        setShowCollaborationNotification(true);

        if (cleanupTimeoutRef.current) {
          clearTimeout(cleanupTimeoutRef.current);
        }
        cleanupTimeoutRef.current = setTimeout(() => {
          setShowCollaborationNotification(false);
        }, 3000);

      } catch (error: unknown) {
        console.error("Failed to start collaboration:", error);

        collaborationInitialized.current = false;
        currentProjectIdRef.current = null;

        const errorMessage = error instanceof Error ? error.message : "";
        const isDuplicateKeyError = errorMessage.includes("E11000 duplicate key error") ||
                                  errorMessage.includes("operations.id_1 dup key");

        if (isDuplicateKeyError) {
          setLastFailedProjectId(projectId);
          setCollaborationMessage(
            "Database conflict detected. Please refresh the page to start fresh collaboration."
          );
        } else {
          setCollaborationMessage(
            "Failed to connect to collaboration server. Working in single-user mode."
          );
        }

        setShowCollaborationNotification(true);
        if (cleanupTimeoutRef.current) {
          clearTimeout(cleanupTimeoutRef.current);
        }
        cleanupTimeoutRef.current = setTimeout(() => {
          setShowCollaborationNotification(false);
        }, 8000);
      }
    };

    checkAndStartCollaboration();
  }, [projectId, currentUser, lastFailedProjectId]);

  // Reset collaboration state when project changes
  useEffect(() => {
    if (currentProjectIdRef.current !== projectId) {
      console.log("Project changed, resetting collaboration state:", {
        from: currentProjectIdRef.current,
        to: projectId
      });

      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }

      collaborationInitialized.current = false;
      setCollaborationAttempted(false);
      setLastFailedProjectId(null);
      setShowCollaborationNotification(false);
    }
  }, [projectId]);

  // Cleanup when component actually unmounts (not on StrictMode remounts)
  useEffect(() => {
    let isRealUnmount = false;

    const timeoutId = setTimeout(() => {
      isRealUnmount = true;
    }, 100);

    return () => {
      clearTimeout(timeoutId);

      if (isRealUnmount) {
        console.log("CollaborationProvider real cleanup initiated");

        if (cleanupTimeoutRef.current) {
          clearTimeout(cleanupTimeoutRef.current);
          cleanupTimeoutRef.current = null;
        }

        collaborationInitialized.current = false;
        currentProjectIdRef.current = null;

        if (collaboration.isActive) {
          console.log("Stopping active collaboration during real cleanup");
          collaboration.stopCollaboration().catch((error) => {
            console.error("Error stopping collaboration during cleanup:", error);
          });
        }
      }
    };
  }, []);

  // Handle cursor updates from editor
  const handleCursorUpdate = useCallback(
    (position: Position) => {
      if (collaboration.isActive) {
        collaboration.sendCursorUpdate(position);
      }
    },
    [collaboration],
  );

  // Handle selection updates from editor — now handled automatically by editor store
  useEffect(() => {
    // Selection updates are now automatically sent via collaboration bridge
  }, [selectedComponents]);

  // Handle merge editor
  const handleConflictResolve = useCallback(
    (conflictId: string, resolution: "merge" | "reject" | "queue") => {
      if (
        resolution === "merge" &&
        collaboration.conflicts.length > 0
      ) {
        const conflict = collaboration.conflicts.find(
          (c) => c.id === conflictId,
        );
        if (conflict) {
          setActiveMergeConflict(conflict);
          setShowMergeEditor(true);
          return;
        }
      }

      conflictResolution.resolveConflict(conflictId, resolution);
    },
    [collaboration.conflicts, conflictResolution],
  );

  // Legacy compatibility methods
  const legacyMethods = {
    isComponentLocked: (componentId: string) => false,
    getComponentLockOwner: (componentId: string): UserSession | null => null,
    requestComponentLock: async (componentId: string) => true,
    releaseComponentLock: (componentId: string) => {},
    handleDragConflict: (componentId: string, action: "start" | "resolve") => {
      if (action === "start") {
        const activeUserIds = collaboration.activeUsers.map((u) => u.userId);
        if (activeUserIds.length > 1) {
          console.log("Multi-user drag conflict detected", { componentId, activeUserIds });
          conflictResolution.createConflict(componentId, activeUserIds);
        } else {
          console.log("Single user editing - no conflict needed", { componentId, activeUserCount: activeUserIds.length });
        }
      }
    },
    getUserCursor: (userId: string) => presence.userCursors.get(userId) || null,
    broadcastCursorPosition: handleCursorUpdate,
    resolveDragConflict: handleConflictResolve,
  };

  const contextValue: CollaborationContextValue = {
    // Connection state from unified collaboration hook
    isActive: collaboration.isActive,
    isConnected: collaboration.isConnected,

    // Users and presence from unified collaboration hook
    activeUsers: collaboration.activeUsers,
    currentUser: collaboration.currentUser,

    // Actions from unified collaboration hook
    startCollaboration: collaboration.startCollaboration,
    stopCollaboration: collaboration.stopCollaboration,
    updateCursor: handleCursorUpdate,
    updateSelection: (componentIds) => {
      // Selection updates are handled automatically by collaboration bridge
    },

    // Legacy compatibility
    ...legacyMethods,
  };

  return (
    <CollaborationContext.Provider value={contextValue}>
      {/* Canvas container with ref for cursor tracking */}
      <div ref={canvasRef} className="relative w-full h-full">
        {children}

        {/* Collaboration UI — enabled */}
        {collaboration.isActive && showUI && (
          <>
            {/* Real-time cursors and selections */}
            <RealtimeCursors
              canvasRef={canvasRef}
              zoom={1}
              offset={{ x: 0, y: 0 }}
            />

            {/* Enhanced presence with real data */}
            <PresenceAdapter
              projectId={projectId}
              showDetailedInfo={showDetailedInfo}
              compactMode={mobileOptimized}
              onUserClick={onUserClick}
            />

            {/* Conflict alerts */}
            <ConflictAlertsContainer
              conflicts={collaboration.conflicts}
              choices={conflictResolution.conflictChoices}
              onResolve={handleConflictResolve}
              onDismiss={(conflictId) =>
                conflictResolution.resolveConflict(conflictId, "reject")
              }
              maxVisible={3}
              position="top-right"
            />

            {/* Merge editor modal */}
            <AnimatePresence>
              {showMergeEditor && activeMergeConflict && (() => {
                const mergeComponent = components.find(
                  (c) => c.id === activeMergeConflict.componentId,
                );
                if (!mergeComponent) return null;
                return (
                <MergeEditorBackdrop onClose={() => setShowMergeEditor(false)}>
                  <MergeEditor
                    conflict={activeMergeConflict}
                    originalComponent={mergeComponent}
                    conflictedVersions={activeMergeConflict.users.map(
                      (userSession) => ({
                        component: mergeComponent,
                        userId: userSession.userId,
                        user: userSession,
                        timestamp: userSession.lastActivity,
                        changes: [
                          {
                            field: "position",
                            oldValue: { x: 0, y: 0, z: 0 },
                            newValue: { x: 10, y: 10, z: 0 },
                          },
                        ],
                      }),
                    )}
                    onResolve={(mergedComponent, resolution) => {
                      setShowMergeEditor(false);
                      setActiveMergeConflict(null);
                      conflictResolution.resolveConflict(
                        activeMergeConflict.id,
                        "merge",
                      );
                    }}
                    onCancel={() => {
                      setShowMergeEditor(false);
                      setActiveMergeConflict(null);
                    }}
                  />
                </MergeEditorBackdrop>
                );
              })()}
            </AnimatePresence>
          </>
        )}

        {/* Collaboration status notification */}
        <AnimatePresence>
          {showCollaborationNotification && (
            <div
              className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-amber-100 border border-amber-400 text-amber-800 px-4 py-2 rounded-lg shadow-lg max-w-md"
              style={{
                animation:
                  "slideInDown 0.3s ease-out, slideOutUp 0.3s ease-in 4.7s forwards",
              }}
            >
              <div className="flex items-center space-x-2">
                <div className="flex-shrink-0">
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="text-sm font-medium">
                  {collaborationMessage}
                </div>
              </div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </CollaborationContext.Provider>
  );
};

export const CollaborationProvider: React.FC<CollaborationProviderProps> = (
  props,
) => {
  return (
    <WebSocketProvider>
      <CollaborationProviderInner {...props} />
    </WebSocketProvider>
  );
};

// Legacy export for backward compatibility
export const useCollaboration = useCollaborationLegacy;
